import { usePortalTranslate } from '@/hooks'
import { Reason } from '@/domain/return-reasons'
import { ChevronDownIcon, ReverCard } from '@itsrever/design-system'
import { useState } from 'react'
import { ReturnReason } from './reason'
import { ReasonsHeader } from './reasons-header'
import { useTranslation } from 'react-i18next'
import { SwitchRow } from '@/ui/components/group-settings'

interface ReasonsCardProps {
  parentReason: Reason
  reasons: Reason[]
  index: number
  handleChangeCategoryIds: (
    categoryIds: string[],
    reasonIndex: number,
    subReasonIndex?: number
  ) => void
  handleChangeMandatoryComment: (
    value: boolean,
    reasonIndex: number,
    subReasonIndex?: number
  ) => void
  // Offers the switch that skips the parent and shows its sub-reasons directly; off without it.
  handleChangeHidden?: (hidden: boolean, reasonIndex: number) => void
  readOnly: boolean
  isShopify: boolean
}

export const ReasonsWithChildren: React.FC<ReasonsCardProps> = ({
  parentReason,
  reasons,
  index,
  handleChangeCategoryIds,
  handleChangeMandatoryComment,
  handleChangeHidden,
  readOnly,
  isShopify
}) => {
  const { t } = useTranslation()
  const { trans } = usePortalTranslate({})
  const [openSubReasons, setOpenSubReasons] = useState(false)

  const handleClickSubReasons = () => {
    setOpenSubReasons((prev) => !prev)
  }

  const changeValues = (values: string[], subReasonIndex?: number) => {
    handleChangeCategoryIds(values, index, subReasonIndex)
  }

  const changeMandatoryComment = (value: boolean, subReasonIndex?: number) => {
    handleChangeMandatoryComment(value, index, subReasonIndex)
  }

  return (
    <ReverCard className="mb-4 p-4" fullWidth>
      <div className="mb-3 border-b border-solid border-gray-300 pb-2">
        <p>
          <b>{trans(`select_reason_${parentReason.reason}`)}</b>
        </p>
      </div>
      {handleChangeHidden ? (
        <div className="mb-3">
          <SwitchRow
            label={t('returns_refunds.reasons.only_sub_reasons')}
            description={t(
              'returns_refunds.reasons.only_sub_reasons_description'
            )}
            checked={parentReason.hidden}
            readOnly={readOnly}
            onChange={(hidden) => handleChangeHidden(hidden, index)}
          />
        </div>
      ) : null}

      <div>
        <div
          className="mb-2 flex cursor-pointer items-center justify-between gap-2 rounded-md bg-blue-200 p-3"
          onClick={handleClickSubReasons}
        >
          <p>{t('edit_reasons.subreasons')}</p>
          <ChevronDownIcon />
        </div>
        {openSubReasons ? (
          <>
            <ReasonsHeader isShopify={isShopify} />
            <ul>
              {reasons.length ? (
                reasons.map((reason, j) => {
                  return (
                    <ReturnReason
                      key={reason.reason}
                      subIndex={j}
                      reason={reason}
                      changeValues={changeValues}
                      changeMandatoryComment={changeMandatoryComment}
                      readOnly={readOnly}
                      isShopify={isShopify}
                    />
                  )
                })
              ) : (
                <ReturnReason
                  reason={parentReason}
                  changeValues={changeValues}
                  changeMandatoryComment={changeMandatoryComment}
                  readOnly={readOnly}
                  isShopify={isShopify}
                />
              )}
            </ul>
          </>
        ) : null}
      </div>
    </ReverCard>
  )
}
